import { useState } from "react";
import {
  conferirValeFalta, diaParaCobrir, formatPoints, sequenciaSalva,
  type ChallengeConfig,
} from "@nhomi/core";
import { usarValeFalta } from "@/data/valeFalta";
import type { Member } from "@/data/members";

/* ============================================================
   O convite pra salvar a sequência

   O vale-falta existia só como número no perfil: "restam 2", sem
   nenhum lugar onde gastar. Este é o lugar — e ele aparece ONDE a
   pessoa está, não numa aba que ela teria que procurar.

   QUANDO ELE APARECE é a decisão inteira. Um cartão que diz "você tem 2
   vale-faltas" todo dia é ruído; um que aparece na manhã seguinte a uma
   falta, dizendo o número exato de dias que voltam, é uma decisão de um
   toque. Por isso ele só existe quando o core diz que dá — mesmo
   `conferirValeFalta` que o servidor chama — e só para ONTEM.

   E ele diz o que se GANHA, não o que se gasta. "Usar um vale-falta?"
   não informa nada: o recurso é escasso, e quem não sabe o que ganha
   guarda para sempre. "Sua sequência de 6 dias volta" é uma conta que a
   pessoa consegue fazer.
   ============================================================ */

export function ValeFalta(
  { cid, config, eu, hoje, startDay, endDay, aoUsar }: {
    cid: string;
    config: ChallengeConfig;
    eu: Member;
    hoje: string;
    startDay: string;
    /** Vazio = desafio sem data de fim. */
    endDay: string;
    /** Para a tela recarregar a ficha sem esperar o listener. */
    aoUsar?: () => void;
  },
) {
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [feito, setFeito] = useState<number | null>(null);

  const dia = diaParaCobrir(hoje);
  const pedido = {
    dia,
    hoje,
    startDay,
    endDay,
    days: eu.days ?? [],
    jokerDays: eu.jokerDays ?? [],
    jokers: eu.jokers ?? 0,
  };

  /* A MESMA conferência que o servidor faz, com os mesmos dados. A tela
     precisa da janela do desafio junto: sem ela o cartão apareceria num
     desafio que já terminou, e o toque viraria um erro que só o servidor
     sabia prever. Quem decide de verdade continua sendo a função — aqui
     é para não convidar para o que vai ser recusado. */
  if (feito === null && conferirValeFalta(pedido)) return null;

  const volta = sequenciaSalva(pedido);
  const liga = volta >= config.rules.streakFrom;

  if (feito !== null) {
    return (
      <div className="pad">
        <div className="card vale-falta feito">
          <div className="vale-falta-t">🃏 Dia coberto</div>
          <p className="vale-falta-p">
            Sua sequência está em {feito} {feito === 1 ? "dia" : "dias"}.
            {" "}Restam {Math.max(0, (eu.jokers ?? 0) - 1)} vale-faltas neste desafio.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="pad">
      <div className="card vale-falta">
        <div className="vale-falta-t">🃏 Você não postou ontem</div>
        <p className="vale-falta-p">
          Um vale-falta cobre o dia e sua sequência volta para{" "}
          <strong>{volta} {volta === 1 ? "dia" : "dias"}</strong>
          {liga && (
            <> — de novo no multiplicador de {formatPoints(config.rules.streakMultiplier)}×</>
          )}
          . Você tem {eu.jokers} neste desafio.
        </p>
        {erro && <div className="hint-row erro">{erro}</div>}
        <button className="btn btn-primary" disabled={ocupado}
          onClick={async () => {
            setOcupado(true);
            setErro(null);
            try {
              const r = await usarValeFalta(cid, dia);
              setFeito(r.sequencia);
              aoUsar?.();
            } catch (e) {
              /* A mensagem do servidor já vem em português e diz o
                 motivo — repetir uma genérica aqui esconderia justamente
                 o que a pessoa precisa ler. */
              setErro((e as { message?: string }).message || "Não deu pra usar agora.");
            }
            setOcupado(false);
          }}>
          {ocupado ? "Um instante…" : "Usar um vale-falta"}
        </button>
      </div>
    </div>
  );
}
